import type { BeadColor } from '../catalog/types';
import { adjustGrid, isNeutral, type ImageAdjustments } from '../import/adjust';
import { EMPTY } from '../pattern/empty';
import { dither } from '../quantize/dither';
import { mergeRareColors } from '../quantize/merge-rare';
import { quantize } from '../quantize/nearest';
import { reduceToAllowedIndices } from '../quantize/reduce';
import type { Grid } from '../quantize/types';
import {
  computePlateLayout,
  computeStockCoverage,
  estimatedTimeMinutes,
  physicalSize,
} from './estimate';
import type { PlateLayout, StockCoverage } from './estimate';
import { resize } from './resize';
import type { ResamplingMode } from './resize';

/** Stratégie de quantification appliquée après redimensionnement. */
export type QuantizationMode = 'nearest' | 'dither';

/**
 * Options de la conversion image → patron.
 */
export type ConversionOptions = {
  /** Dimensions de la grille cible, en nombre de perles. */
  targetWidth: number;
  targetHeight: number;
  /** Palette de travail : couleurs autorisées sur ce projet, ordre stable. */
  palette: ReadonlyArray<BeadColor>;
  /** Méthode de redimensionnement de l'image source. */
  resampling: ResamplingMode;
  /** Stratégie de quantification (avec ou sans Floyd-Steinberg). */
  quantization: QuantizationMode;
  /**
   * Intensité du tramage ∈ [0, 1] quand `quantization === 'dither'`
   * (Lot P2 — slider « Détails » du tunnel). 1 par défaut = Floyd-Steinberg
   * classique ; 0 ≡ nearest. Ignoré en mode 'nearest'.
   */
  ditherStrength?: number;
  /**
   * Plafond du nombre de couleurs du patron (Lot P2 — un sachet par couleur
   * dans le kit). La quantification est contrainte aux `maxColors` couleurs
   * de la palette qui représentent le mieux l'image (median-cut, cf.
   * reduce.ts). Absent = palette complète.
   */
  maxColors?: number;
  /**
   * Fusion des couleurs rares (Lot P2) : toute couleur avec moins de
   * `mergeBelowCount` perles est réassignée à la couleur conservée la plus
   * proche. Absent ou ≤ 1 = pas de fusion.
   */
  mergeBelowCount?: number;
  /**
   * Restriction DURE de la palette à un sous-ensemble d'ids (Lot P3 —
   * garantie « Noir & blanc » : seules les couleurs neutres autorisées, sinon
   * le tramage/nearest peut poser une perle colorée pour approcher un gris).
   * Les ids inconnus sont ignorés ; si `maxColors` est plus petit que le
   * sous-ensemble, on garde les couleurs les plus étalées en luminance. Absent
   * = comportement normal (median-cut sur toute la palette).
   */
  allowedColorIds?: ReadonlyArray<string>;
  /**
   * Réglages image amont (Lot P2 — presets de style du tunnel), appliqués
   * sur la grille source AVANT le redimensionnement.
   */
  adjustments?: ImageAdjustments;
  /**
   * Dimensions d'une plaque physique en perles.
   * Par défaut : 29×29 (plaque Hama Midi carrée standard, cf. §14.2).
   */
  plate?: { width: number; height: number };
  /**
   * Inventaire cumulé déclaré par l'utilisateur (colorId → nombre de perles dispo).
   * Si fourni, le pattern incluera `stockCoverage`.
   */
  inventory?: ReadonlyMap<string, number>;
};

/**
 * Résultat complet du pipeline : grille quantifiée + métadonnées d'agencement,
 * estimations physiques/temporelles, et alerte stock (si inventaire fourni).
 *
 * Forme proche du `Project` Firestore défini au §4.3 du cahier des charges,
 * à l'exception des métadonnées utilisateur (id, ownerId, dates…).
 */
export type Pattern = {
  width: number;
  height: number;
  /** cells[y * width + x] = index dans paletteColorIds. */
  cells: ReadonlyArray<number>;
  /** Mapping index → BeadColor.id (l'ordre est figé dans le pattern). */
  paletteColorIds: ReadonlyArray<string>;
  /** Comptage des perles utilisées par couleur. */
  counts: ReadonlyMap<string, number>;
  /** Agencement physique des plaques. */
  layout: PlateLayout;
  /** Dimensions physiques en cm (pas de 5 mm par perle Hama Midi). */
  physicalSize: { widthCm: number; heightCm: number };
  /** Estimation indicative du temps de pose à 2 sec/perle. */
  estimatedTimeMinutes: number;
  /** Couverture par l'inventaire utilisateur (présent ssi `options.inventory` fourni). */
  stockCoverage?: StockCoverage;
};

const DEFAULT_PLATE_WIDTH = 29;
const DEFAULT_PLATE_HEIGHT = 29;

/** Indices palette d'un sous-ensemble d'ids (ordre palette, dédoublonné).
 *  undefined si aucun id fourni ou aucun ne correspond → comportement normal. */
function restrictIndices(
  palette: ReadonlyArray<BeadColor>,
  ids: ReadonlyArray<string> | undefined,
): ReadonlyArray<number> | undefined {
  if (ids === undefined || ids.length === 0) return undefined;
  const wanted = new Set(ids);
  const idx: number[] = [];
  for (let i = 0; i < palette.length; i++) {
    const c = palette[i];
    if (c && wanted.has(c.id)) idx.push(i);
  }
  return idx.length > 0 ? idx : undefined;
}

/** Perceptual luminance (Rec. 709) d'une couleur palette à un index donné. */
function lumaAt(palette: ReadonlyArray<BeadColor>, i: number): number {
  const c = palette[i];
  if (!c) return 0;
  return 0.2126 * c.rgb.r + 0.7152 * c.rgb.g + 0.0722 * c.rgb.b;
}

/** Garde `count` indices étalés en luminance (extrêmes inclus) parmi `indices`. */
function spreadByLuma(
  indices: ReadonlyArray<number>,
  palette: ReadonlyArray<BeadColor>,
  count: number,
): ReadonlyArray<number> {
  if (count >= indices.length) return indices;
  if (count <= 1) return indices.length > 0 ? [indices[0] as number] : [];
  const sorted = [...indices].sort((a, b) => lumaAt(palette, a) - lumaAt(palette, b));
  const out: number[] = [];
  for (let k = 0; k < count; k++) {
    const pos = Math.round((k * (sorted.length - 1)) / (count - 1));
    const v = sorted[pos];
    if (v !== undefined && !out.includes(v)) out.push(v);
  }
  return out;
}

/**
 * Pipeline complet image → patron de perles.
 *
 * Étapes :
 *  0. (P2) Réglages image amont (brightness/contrast/saturation — presets)
 *  1. Redimensionnement (nearest ou bilinéaire)
 *  2. (P2) Réduction du nombre de couleurs (median-cut → sous-ensemble palette)
 *  3. Quantification (nearest pur ou Floyd-Steinberg à intensité variable),
 *     contrainte au sous-ensemble si maxColors
 *  4. (P2) Fusion des couleurs rares
 *  5. Calcul du layout des plaques physiques
 *  6. Estimations (dimensions cm, temps, comptage)
 *  7. Couverture stock si inventaire fourni
 */
export function imageToPattern(source: Grid, options: ConversionOptions): Pattern {
  const { targetWidth, targetHeight, palette } = options;
  if (palette.length === 0) {
    throw new Error('imageToPattern : palette vide.');
  }
  if (targetWidth <= 0 || targetHeight <= 0) {
    throw new Error(`imageToPattern : dimensions cibles invalides ${targetWidth}×${targetHeight}.`);
  }

  // 0 : réglages image (no-op si neutres)
  const adjusted = isNeutral(options.adjustments)
    ? source
    : adjustGrid(source, options.adjustments ?? {});

  // 1 : resize
  const resized = resize(adjusted, targetWidth, targetHeight, options.resampling);

  // 2 : réduction du nombre de couleurs (optionnelle)
  const restrictIds = restrictIndices(palette, options.allowedColorIds);
  let allowed: ReadonlyArray<number> | undefined;
  if (restrictIds !== undefined) {
    // Restriction dure (ex. N&B) : on part du sous-ensemble d'ids et on le
    // borne éventuellement à maxColors en gardant l'étalement de luminance.
    allowed =
      options.maxColors !== undefined && options.maxColors < restrictIds.length
        ? spreadByLuma(restrictIds, palette, options.maxColors)
        : restrictIds;
  } else if (options.maxColors !== undefined && options.maxColors < palette.length) {
    allowed = reduceToAllowedIndices(resized, palette, options.maxColors);
  }

  // 3 : quantification (contrainte au sous-ensemble si présent)
  let result =
    options.quantization === 'dither'
      ? dither(resized, palette, options.ditherStrength ?? 1, allowed)
      : quantize(resized, palette, allowed);

  // 4 : fusion des couleurs rares (optionnelle)
  if (options.mergeBelowCount !== undefined && options.mergeBelowCount > 1) {
    result = mergeRareColors(result, palette, options.mergeBelowCount);
  }

  // 5 : layout
  const plateWidth = options.plate?.width ?? DEFAULT_PLATE_WIDTH;
  const plateHeight = options.plate?.height ?? DEFAULT_PLATE_HEIGHT;
  const layout = computePlateLayout(targetWidth, targetHeight, plateWidth, plateHeight);

  // 6 : estimations
  const totalBeads = targetWidth * targetHeight;
  const size = physicalSize(targetWidth, targetHeight);
  const timeMin = estimatedTimeMinutes(totalBeads);

  // 7 : stock (optionnel)
  const base: Pattern = {
    width: result.width,
    height: result.height,
    cells: result.cells,
    paletteColorIds: palette.map((c) => c.id),
    counts: result.counts,
    layout,
    physicalSize: size,
    estimatedTimeMinutes: timeMin,
  };

  if (options.inventory !== undefined) {
    return { ...base, stockCoverage: computeStockCoverage(result.counts, options.inventory) };
  }
  return base;
}

/**
 * Marque comme EMPTY toutes les cellules pour lesquelles `mask[i] === true`,
 * et recompose `counts` (décrémente chaque couleur dont une cellule a été
 * mise à vide).
 *
 * Usage type : après `imageToPattern` sur une source PNG/SVG avec transparence,
 * pour matérialiser les zones transparentes comme « pas de perle ».
 *
 * @throws si `mask.length !== pattern.cells.length`.
 */
export function applyEmptyMask(pattern: Pattern, mask: ReadonlyArray<boolean>): Pattern {
  if (mask.length !== pattern.cells.length) {
    throw new Error(
      `applyEmptyMask : mask.length (${mask.length}) != cells.length (${pattern.cells.length}).`,
    );
  }
  const newCells = pattern.cells.slice();
  const newCounts = new Map(pattern.counts);
  let changed = false;
  for (let i = 0; i < newCells.length; i++) {
    if (!mask[i]) continue;
    const idx = newCells[i];
    if (idx === undefined || idx < 0) continue; // déjà vide ou hors plage
    newCells[i] = EMPTY;
    changed = true;
    const colorId = pattern.paletteColorIds[idx];
    if (colorId !== undefined) {
      newCounts.set(colorId, Math.max(0, (newCounts.get(colorId) ?? 0) - 1));
    }
  }
  if (!changed) return pattern;
  return { ...pattern, cells: newCells, counts: newCounts };
}
